// Runtime configuration loader
// This allows the same build to work across different domains

interface DomainConfig {
  apiUrl: string;
  brandName: string;
  logoUrl: string;
}

interface RuntimeConfig {
  apiUrl: {
    [domain: string]: string;
    default: string;
  };
  branding: {
    [domain: string]: {
      name: string;
      logo: string;
    };
  };
}

class ConfigManager {
  private static instance: ConfigManager;
  private config: RuntimeConfig | null = null;
  private configPromise: Promise<RuntimeConfig> | null = null;

  private constructor() {}

  static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  async loadConfig(): Promise<RuntimeConfig> {
    // If already loading, return the existing promise
    if (this.configPromise) {
      return this.configPromise;
    }

    // If already loaded, return the config
    if (this.config) {
      return Promise.resolve(this.config);
    }

    // Start loading
    this.configPromise = fetch('/config.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load configuration');
        }
        return response.json();
      })
      .then(config => {
        this.config = config;
        return config;
      })
      .catch(error => {
        console.error('Failed to load runtime config, using defaults:', error);
        // Fallback configuration
        const fallbackConfig: RuntimeConfig = {
          apiUrl: {
            'localhost': 'http://localhost:8080/api/v1',
            'portal.iatelecom.ca': 'https://apito.iatelecom.ca/api/v1',
            'portal.ourbizphone.com': 'https://apito.iatelecom.ca/api/v1',
            'default': 'https://apito.iatelecom.ca/api/v1'
          },
          branding: {
            'portal.iatelecom.ca': {
              name: 'Inter-Active Telecom',
              logo: '/assets/iatelecom-logo.png'
            },
            'portal.ourbizphone.com': {
              name: 'OurBizPhone',
              logo: '/assets/ourbizphone-logo.png'
            }
          }
        };
        this.config = fallbackConfig;
        return fallbackConfig;
      });

    return this.configPromise;
  }

  async getDomainConfig(): Promise<DomainConfig> {
    const config = await this.loadConfig();
    const hostname = window.location.hostname;
    
    // Get API URL for current domain
    const apiUrl = config.apiUrl[hostname] || config.apiUrl.default;
    
    // Get branding for current domain
    const branding = config.branding[hostname] || {
      name: 'Portal',
      logo: '/assets/default-logo.png'
    };

    return {
      apiUrl,
      brandName: branding.name,
      logoUrl: branding.logo
    };
  }

  async getApiUrl(): Promise<string> {
    const config = await this.getDomainConfig();
    return config.apiUrl;
  }

  async getBranding(): Promise<{ name: string; logo: string }> {
    const config = await this.getDomainConfig();
    return {
      name: config.brandName,
      logo: config.logoUrl
    };
  }
}

// Export singleton instance
export const configManager = ConfigManager.getInstance();

// Helper functions for easy access
export const getApiUrl = () => configManager.getApiUrl();
export const getBranding = () => configManager.getBranding();
export const getDomainConfig = () => configManager.getDomainConfig();